import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { taxIdInfo } from "@/lib/vkn";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ShieldCheck, Loader2, RefreshCw, AlertTriangle, BadgeCheck, Archive } from "lucide-react";

const Row = ({ k, v, tid }) => (
  <div className="flex justify-between gap-3 text-[11px]"><span className="text-white/60">{k}</span><span className="font-semibold text-right break-all" data-testid={tid}>{v || "—"}</span></div>
);

// Canlı QNB mükellef sorgusu: geçerli VKN/TCKN girilince otomatik çalışır → e-Fatura mükellefi mi, e-Arşiv mi?
export function TaxpayerCard({ vkn, title, onResult }) {
  const [st, setSt] = useState({ busy: false, data: null, err: "" });
  const seq = useRef(0);
  const info = taxIdInfo(vkn);
  const run = async () => {
    const my = ++seq.current;
    setSt({ busy: true, data: null, err: "" });
    try {
      const { data } = await api.post("/qnb/check-taxpayer", { vkn, contact_title: title || undefined });
      if (my !== seq.current) return;
      setSt({ busy: false, data, err: "" });
      onResult && onResult(data);
    } catch (e) {
      if (my !== seq.current) return;
      setSt({ busy: false, data: null, err: apiError(e) });
      onResult && onResult(null);
    }
  };
  useEffect(() => {
    seq.current += 1;
    setSt({ busy: false, data: null, err: "" });
    onResult && onResult(null);
    if (info.valid !== true) return undefined;
    const t = setTimeout(run, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vkn]);

  if (!vkn) return <p className="rounded-xl border border-dashed px-3 py-2.5 text-[11px] text-muted-foreground flex items-center gap-2" data-testid="ci-taxpayer-empty"><ShieldCheck className="h-4 w-4 shrink-0" />{title ? "Carinin VKN/TCKN'si kayıtlı değil — kesim ekranında girilince mükellef sorgusu yapılır." : "VKN/TCKN girilince QNB mükellef sorgusu otomatik yapılır."}</p>;
  if (info.valid === false) return <p className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-[11px] text-amber-900 flex items-center gap-2" data-testid="ci-taxpayer-invalid"><AlertTriangle className="h-4 w-4 shrink-0" />{info.message}</p>;
  if (st.busy || (!st.data && !st.err)) return <p className="rounded-xl border bg-muted/50 px-3 py-2.5 text-[11px] text-muted-foreground flex items-center gap-2" data-testid="ci-taxpayer-loading"><Loader2 className="h-4 w-4 animate-spin shrink-0" />QNB'den canlı mükellef sorgusu yapılıyor · {vkn}</p>;
  if (st.err) return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[11px] text-red-800 flex items-center gap-2" data-testid="ci-taxpayer-error">
      <AlertTriangle className="h-4 w-4 shrink-0" /><span className="flex-1">{st.err}</span>
      <button type="button" onClick={run} className="font-bold underline" data-testid="ci-taxpayer-retry">Tekrar sorgula</button>
    </div>
  );
  const d = st.data;
  const ef = d.channel === "efatura";
  return (
    <div className={cn("rounded-xl text-white p-3.5 space-y-2 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-300", ef ? "bg-indigo-700" : "bg-sky-700")} data-testid="ci-taxpayer-card">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-head font-black tracking-wide text-sm" data-testid="ci-taxpayer-channel">{ef ? <BadgeCheck className="h-4 w-4" /> : <Archive className="h-4 w-4" />}{ef ? "e-FATURA kesilecek" : "e-ARŞİV kesilecek"}{d.stale && <span className="rounded bg-amber-400/30 px-1.5 text-[9px]">SON SORGU</span>}</span>
        <button type="button" onClick={run} className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 px-2 py-0.5 text-[10px] font-bold transition-colors" data-testid="ci-taxpayer-refresh"><RefreshCw className="h-3 w-3" />Yenile</button>
      </div>
      <p className="text-[11px] text-white/75 leading-snug">{ef ? "Alıcı GİB'de kayıtlı e-Fatura mükellefi." : "Alıcı e-Fatura mükellefi değil — e-Arşiv fatura düzenlenir."}</p>
      <div className="space-y-1 border-t border-white/15 pt-2">
        <Row k="VKN / TCKN" v={d.vkn} tid="ci-taxpayer-vkn" />
        {ef && <Row k="GİB ünvanı" v={d.unvan} tid="ci-taxpayer-unvan" />}
        {ef && d.etiket && <Row k="Posta kutusu" v={d.etiket} tid="ci-taxpayer-etiket" />}
        {ef && d.kayit_zamani && <Row k="e-Fatura kayıt" v={fmtDate(d.kayit_zamani)} tid="ci-taxpayer-kayit" />}
      </div>
      {d.warning && <p className="text-[10.5px] rounded-md bg-amber-400/20 px-2 py-1 text-amber-50" data-testid="ci-taxpayer-warning">{d.warning}</p>}
    </div>
  );
}
